"use client"

/**
 * The membership-first site's mobile menu (see `./navbar-membership-first.tsx`):
 * Check-ups ends on an Add-ons link to the pricing page instead of today's
 * full add-on list (`./mobile-menu.tsx`).
 */

import { useState, useEffect } from "react"
import { m, AnimatePresence } from "framer-motion"
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import Link from "next/link"
import { cn } from "@/lib/utils"
import { localeLabels } from "@/i18n/config"
import { track } from "@/lib/analytics"
import { Button } from "@/components/ui/button"
import { ChevronDownIcon, SearchIcon } from "@/components/ui/icons"
import { useCountry } from "@/components/layout/country-provider"
import { isAllowedInCountry } from "@/lib/country-gates"
import { useSearchModal } from "@/components/search/search-provider"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { usePickerCountries } from "@/lib/use-preview-mode"
import type { Country } from "@/i18n/config"
import { LocaleGrid, shouldShowRegionGrid } from "./locale-picker"
import { CountryGate } from "@/components/layout/country-gate"
import type { NavbarItem } from "./types"

function LocaleDropdown({
  locale,
  countryConfig,
  switchLocale,
  setMobileOpen,
}: {
  locale: string
  countryConfig: { code: Country }
  switchLocale: (country: string, language: string) => void
  setMobileOpen: (open: boolean) => void
}) {
  const [open, setOpen] = useState(false)
  // Defaults to `publicCountries` for SSR; widens client-side when the
  // visitor has a preview-active cookie OR is on a non-public country.
  const pickerCountries = usePickerCountries(countryConfig.code)
  const showRegionGrid = shouldShowRegionGrid(pickerCountries)

  function handleSelect(country: Country, lang: string) {
    if (country === countryConfig.code && lang === locale) {
      setOpen(false)
      return
    }
    track.localeChanged({
      surface: "navbar-mobile",
      from_country: countryConfig.code,
      from_locale: locale,
      to_country: country,
      to_locale: lang,
      page_path: window.location.pathname,
    })
    switchLocale(country, lang)
    setMobileOpen(false)
  }

  // CH-only fallback — the exact pre-redesign mobile dropdown (label-only
  // flat list). Byte-for-byte identical to today so nothing changes in
  // production until NL/DE launches.
  if (!showRegionGrid) {
    const otherLocales = pickerCountries.flatMap((cc) =>
      cc.languages
        .filter((lang) => !(cc.code === countryConfig.code && lang === locale))
        .map((lang) => ({ country: cc, lang }))
    )
    return (
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="type-body-lg inline-flex h-10 cursor-pointer items-center gap-2 rounded-full py-2 pr-2 pl-4 text-neutral-900 transition-colors hover:text-neutral-900"
        >
          {localeLabels[locale] || "EN"}
          <ChevronDownIcon
            width={24}
            height={24}
            className={cn(
              "transition-transform duration-200",
              open && "rotate-180"
            )}
          />
        </button>
        {/* safe-animate-presence: conditional-mount — child only renders when `open=true` */}
        <AnimatePresence>
          {open && (
            <m.div
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 4 }}
              transition={{ duration: 0.15 }}
              className="absolute bottom-full left-0 z-10 mb-1 min-w-[90px] overflow-hidden rounded-[16px] bg-white shadow-lg"
            >
              {otherLocales.map(({ country: cc, lang }) => (
                <button
                  key={`${cc.code}-${lang}`}
                  type="button"
                  onClick={() => handleSelect(cc.code, lang)}
                  className="type-body-lg flex h-10 w-full cursor-pointer items-center py-2 pr-2 pl-4 text-neutral-900 transition-colors hover:bg-neutral-100"
                >
                  {localeLabels[lang]}
                </button>
              ))}
            </m.div>
          )}
        </AnimatePresence>
      </div>
    )
  }

  return (
    <div className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="type-body-lg inline-flex h-10 cursor-pointer items-center gap-2 rounded-full py-2 pr-2 pl-4 text-neutral-900 transition-colors hover:text-neutral-900"
      >
        {localeLabels[locale] || "EN"}
        <ChevronDownIcon
          width={24}
          height={24}
          className={cn(
            "transition-transform duration-200",
            open && "rotate-180"
          )}
        />
      </button>
      {/* safe-animate-presence: conditional-mount — child only renders when `open=true` */}
      <AnimatePresence>
        {open && (
          <m.div
            initial={{ opacity: 0, y: 6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.98 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            style={{ transformOrigin: "bottom right" }}
            className="absolute right-0 bottom-full z-10 mb-2 w-[17rem] rounded-[16px] bg-white p-1.5 shadow-lg"
          >
            <LocaleGrid
              countries={pickerCountries}
              currentCountry={countryConfig.code}
              currentLocale={locale}
              onSelect={handleSelect}
              layout="stacked"
            />
          </m.div>
        )}
      </AnimatePresence>
    </div>
  )
}

interface MobileMenuProps {
  items: NavbarItem[]
  ctaLabel?: string
  ctaHref?: string
  onCtaClick?: () => void
  setMobileOpen: (open: boolean) => void
  switchLocale: (country: string, language: string) => void
}

export function MobileMenu({
  items,
  ctaLabel,
  ctaHref,
  onCtaClick,
  setMobileOpen,
  switchLocale,
}: MobileMenuProps) {
  const locale = useLocale()
  const localePath = useLocalePath()
  const countryConfig = useCountry()
  const t = useTranslations("nav")
  const searchT = useTranslations("search")
  const { open: openSearch } = useSearchModal()
  // The bar no longer lists Pricing (its slot is Scans', see
  // useMembershipFirstNavItems), so Check-ups' Add-ons link points at the page
  // itself wherever the country has one.
  const pricingItem = isAllowedInCountry("page:pricing", countryConfig.code)
    ? { href: localePath("/pricing") }
    : undefined
  // Skip the slide-out exit animation when the menu is closing because the
  // search trigger was tapped — we're handing the screen off to the
  // full-bleed search modal and any visible menu transition reads as a
  // flicker between two surfaces. Set right before `setMobileOpen(false)`
  // so the m.div re-renders with `transition: { duration: 0 }`, which is
  // what AnimatePresence then uses for the exit.
  const [instantExit, setInstantExit] = useState(false)

  // Hide the floating chat launcher while the mobile menu is open. Toggling
  // `data-mobile-menu-open` on <html> drives the CSS rule in globals.css.
  useEffect(() => {
    const root = document.documentElement
    root.dataset.mobileMenuOpen = "true"
    return () => {
      delete root.dataset.mobileMenuOpen
    }
  }, [])

  return (
    <>
      {/* Backdrop. No blur below sm: the panel is full width there and
          covers it once open, so the blur only cost GPU time during the
          slide, which janked on Android (AHD-14173). */}
      <m.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: instantExit ? 0 : 0.25 }}
        className="fixed inset-0 z-[2147483646] bg-black/30 min-[1200px]:hidden sm:backdrop-blur-sm"
        onClick={() => setMobileOpen(false)}
      />
      {/* Sidebar */}
      <m.div
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={
          instantExit
            ? { duration: 0 }
            : { duration: 0.3, ease: [0.16, 1, 0.3, 1] }
        }
        className="bg-background fixed top-0 right-0 z-[2147483647] flex h-full w-full flex-col shadow-2xl min-[1200px]:hidden sm:w-[35%] sm:min-w-[375px]"
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        {/* Sidebar header — matches navbar pl-6 pr-[10px] h-16. The
         *  pl-[22px] aligns the *visible left edge* of the magnifying
         *  glass with the menu items' text edge below: parent px-4 +
         *  link px-4 = 32px, minus the button's p-2 (8px) and the
         *  ~2.3px of built-in left whitespace inside the icon's
         *  24-unit viewBox (circle starts at x=3 with stroke). */}
        <div className="flex h-16 items-center justify-between pr-[10px] pl-[22px]">
          {/* Search — replaces the Ahead logo so the mobile menu's left
           *  affordance is a tappable search trigger. Closes the menu first
           *  so closing the search modal lands the user back on the page,
           *  not on a still-open sidebar. */}
          <button
            type="button"
            onClick={() => {
              // `instantExit` flips before the unmount so AnimatePresence
              // exits with `duration: 0` instead of the 300ms slide-out.
              // `instant: true` skips the modal's enter animation. Together
              // the two surfaces hand off in a single frame.
              setInstantExit(true)
              setMobileOpen(false)
              track.searchOpened({
                source: "navbar-mobile",
                country: countryConfig.code,
                locale,
              })
              openSearch({ instant: true, source: "navbar-mobile" })
            }}
            aria-label={searchT("openLabel")}
            className="cursor-pointer p-2 text-neutral-600 transition-colors hover:text-neutral-900"
          >
            <SearchIcon width={28} height={28} />
          </button>
          <button
            type="button"
            className="cursor-pointer p-2 text-neutral-600 transition-colors hover:text-neutral-900"
            aria-label="Close menu"
            onClick={() => setMobileOpen(false)}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M15 5L5 15M5 5l10 10"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Sidebar content */}
        <div className="flex flex-1 flex-col gap-6 overflow-y-auto px-4 pt-0 pb-4">
          {/* Account links — Login + Membership grouped tightly so the
           *  rhythm from the search-icon header through these two rows
           *  reads as one even cadence (each step ~28px between text
           *  baselines). gap-2 inside a py-2.5 link is what produces that.
           *  Login gated to CH only — DE/NL don't have local login pages yet. */}
          <div className="flex flex-col gap-2">
            <CountryGate id="feature:nav-login">
              <a
                href={`https://my.aheadhealth.com/${locale}/sign_in`}
                className="type-title-lg flex items-center gap-2 rounded-xl px-4 py-2.5 tracking-[0] text-neutral-900 transition-colors hover:bg-white"
                onClick={() => setMobileOpen(false)}
              >
                {t("login")}
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  className="shrink-0 translate-y-[2px] text-neutral-300"
                >
                  <path
                    d="M1 7h12M8 2l5 5-5 5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </a>
            </CountryGate>
            {/* No "Ahead membership" link: the new site drops /membership,
                the plan is the product (Michèle, 09.10). */}
          </div>
          {/* Sections with children */}
          {items
            .filter((item) => item.children)
            .map((item) => (
              <div key={item.href}>
                <p className="type-label-md mb-2 px-4 text-neutral-400">
                  {item.label}
                </p>
                <div className="flex flex-col gap-0.5">
                  {item.children!.map((child) => (
                    <Link
                      key={child.href}
                      href={child.href}
                      className="type-title-lg text-foreground block rounded-xl px-4 py-2.5 tracking-[0] transition-colors hover:bg-white"
                      onClick={() => setMobileOpen(false)}
                    >
                      {child.label}
                    </Link>
                  ))}
                </div>
                {/* Keep pricing reachable without a long expanded add-on list. */}
                {item.megaMenuType === "whatWeOffer" && pricingItem && (
                  <Link
                    href={pricingItem.href}
                    className="type-title-lg text-foreground block rounded-xl px-4 py-2.5 tracking-[0] transition-colors hover:bg-white"
                    onClick={() => setMobileOpen(false)}
                  >
                    {t("addOns")}
                  </Link>
                )}
              </div>
            ))}
          {/* Standalone items grouped together, as on desktop (Nadjib,
              bugbash 14.10): Scans in Pricing's old place (Eugene, 20.10). */}
          <div className="flex flex-col gap-0.5">
            {items
              .filter((item) => !item.children && !item.hideOnMobile)
              .map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  prefetch
                  className="type-title-lg text-foreground block rounded-xl px-4 py-2.5 tracking-[0] transition-colors hover:bg-white"
                  onClick={() => setMobileOpen(false)}
                >
                  {item.label}
                </Link>
              ))}
          </div>
        </div>

        {/* Sidebar footer */}
        <div className="flex items-center gap-2 border-t border-neutral-100 px-4 py-6">
          {ctaLabel &&
            (onCtaClick || ctaHref) &&
            (onCtaClick ? (
              // Same nested-<button> trap as the desktop CTA in ./index.tsx —
              // `Button` is itself a <button>, so the wrapper made invalid
              // HTML. Kept flex-1 on the Button so the layout is unchanged.
              <Button
                className="bg-aubergine-800 hover:bg-aubergine-900 w-full flex-1"
                onClick={() => {
                  onCtaClick()
                  setMobileOpen(false)
                }}
              >
                {ctaLabel}
              </Button>
            ) : (
              <Link
                href={ctaHref!}
                className="flex-1"
                onClick={() => {
                  track.ctaClick(ctaLabel, "navbar-mobile", ctaHref!)
                  setMobileOpen(false)
                }}
              >
                <Button className="bg-aubergine-800 hover:bg-aubergine-900 w-full">
                  {ctaLabel}
                </Button>
              </Link>
            ))}
          <LocaleDropdown
            locale={locale}
            countryConfig={countryConfig}
            switchLocale={switchLocale}
            setMobileOpen={setMobileOpen}
          />
        </div>
      </m.div>
    </>
  )
}
